import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FEATURE_MENU_GROUPS, getFeature, getFeatures } from '../lib/features';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { FeatureIcon } from '../components/FeatureIcon';
import { SectionIntro } from '../components/alt/SectionIntro';
import { TALK_TO_SALES_URL } from '../lib/links';
import { ScrubVideoPlate, CLOSING_VIDEO, CLOSING_POSTER } from '../components/ScrubVideoPlate';
import { TeamSharingVisual } from './GigaForChatGPT2';

/**
 * ONE TEMPLATE, EVERY FEATURE PAGE.
 *
 * Tracks, Groups, Assistants, Skills, Pulses and Workflows all render through
 * here, so the shape of this file is the shape of the whole Basics section.
 *
 * What it used to be: an ink hero with two large orange radial blurs behind
 * centred type, then every section of the feature — however many, whatever they
 * said — poured into identically-chromed white rounded cards two across. Two
 * problems with that. The gradient hero belongs to no other page on the site,
 * so a reader arriving from the home page landed somewhere that looked like a
 * different product. And a grid of same-weight bordered boxes is the absence of
 * a decision: it gives the thesis of the feature exactly as much visual weight
 * as its seventh supporting detail.
 *
 * What it is now, in the home page's own language — cream ground, ink display
 * type, one orange accent, hairlines instead of boxes:
 *
 *   1. Hero. Left-aligned like every section intro on the home page, and it
 *      finally uses all three copy fields the data has always carried. The
 *      `eyebrow` (a written promise — "Keep the state of play alive") was never
 *      rendered at all; it is now the lead line under the name, with `summary`
 *      as the explanation beneath it. Name → promise → explanation is a real
 *      hierarchy; a name and one paragraph was not.
 *   2. The thesis. The first section is the argument the feature makes, so it
 *      gets display type and the width of the page, not a card.
 *   3. The detail. Everything after it is a hairline-ruled two-column list —
 *      heading left, body right. It reads as a specification, which is what
 *      this content is, and whitespace does the grouping that seven borders
 *      were doing badly.
 *   4. Where to go next. The sibling features in the same menu group, then the
 *      same closing panel and offer the home page ends on.
 *
 * NO CONTENT CHANGED. Every string still comes from lib/features.ts.
 */
/**
 * `slugOverride` exists so a feature can be given a BESPOKE page while this
 * template rendering of it stays reachable as an archive. AI Agents is the
 * first: `/features/ai-agents` is a purpose-built page, and
 * `/features/ai-agents/alt` mounts this component with the slug passed in
 * rather than read from the URL. Nothing is duplicated — the archive renders
 * the same data through the same template.
 */
export function FeatureDetail({
  slugOverride
}: {
  slugOverride?: string;
} = {}) {
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? '';
  const item = getFeature(slug);
  if (!item) return <Navigate to="/" replace data-id="element-9989" data-mp="52bgai0rzra6" />;
  const items = getFeatures();

  // The menu group this feature belongs to ("The Basics", "Capabilities",
  // "Connect"). Shown in the hero so the reader knows where they are inside the
  // product rather than just which page they opened, and used to pick the
  // siblings at the foot of the page.
  const group = FEATURE_MENU_GROUPS.find(g => (g.slugs as readonly string[]).includes(slug));
  const siblings = (group?.slugs ?? []).filter(s => s !== slug).map(s => getFeature(s)).filter((f): f is NonNullable<typeof f> => Boolean(f)).slice(0, 6);
  const next = items[(items.findIndex(feature => feature.slug === slug) + 1) % items.length];
  const [lead, ...rest] = item.sections;
  return <>
      {/* ── Hero ──────────────────────────────────────────────────────────
       Cream, not ink. The whole site is a warm paper ground with one orange
       accent; a dark hero with two blurred orange casts behind it was the only
       place that language broke, and it broke it on the first screen. */}
      <section className="relative overflow-hidden bg-[#FAF8F4] pb-16 pt-32 md:pb-24 md:pt-40" data-id="element-9990" data-mp="1x164cl0xrk5n">
        <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.5]" data-id="element-9991" data-mp="21uighvofhyhi" />
        {/* The one warm wash, kept far off to the right and very soft, so it
         reads as paper catching light rather than as a coloured blob. */}
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-24 hidden size-[560px] rounded-full bg-[#EA580C]/[0.05] blur-[120px] md:block" data-id="element-9992" data-mp="2fsqk2mfyzy1n" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-9993" data-mp="lez2n0reg9vn">
          <SectionIntro align="left" className="max-w-3xl" data-id="element-9994" data-mp="lgsaukhnv25r">
            {/* Where you are, before what it is. */}
            {/* Group, then this page — a trail ends where you are. It used to
             read "Products — Features", which named the menu and the section
             but never the page, so every feature had the same breadcrumb. */}
            <div className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.16em] text-[#A2917F]" data-id="element-9995" data-mp="946oxfhr2n4b">
              <span data-id="element-9996" data-mp="46of7mvws3v9">{group?.label ?? 'Feature'}</span>
              <span aria-hidden="true" className="h-px w-5 bg-[#D9CFBE]" data-id="element-9997" data-mp="43siu2zpln8x" />
              <span className="text-[#EA580C]" data-id="element-9998" data-mp="11sm17u9hyrby">{item.name}</span>
            </div>

            <div className="mt-7 flex items-center gap-4" data-id="element-9999" data-mp="wvlt7dt37ei7">
              <span aria-hidden="true" className="flex size-12 shrink-0 items-center justify-center rounded-2xl border border-[#EAE0D2] bg-white text-[#EA580C] shadow-[0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-10000" data-mp="ruxyah1qmdip">
                <FeatureIcon name={item.icon} size={23} data-id="element-10001" data-mp="3clycmhouskwp" />
              </span>
              <h1 className="font-title text-[40px] font-bold leading-[1.02] tracking-[-0.03em] text-[#2B211A] sm:text-[52px] md:text-[64px]" data-id="element-10002" data-mp="38juytmepp489">
                {item.name}
              </h1>
            </div>

            {/* The promise, then the explanation. Two different jobs, so two
             different sizes — previously the promise was not on the page. */}
            <p className="mt-8 font-title text-[24px] font-bold leading-[1.22] tracking-[-0.02em] text-[#2B211A] md:text-[30px]" data-id="element-10003" data-mp="302b2lrh2sejn">
              {item.eyebrow}
            </p>
            <p className="mt-5 max-w-[62ch] text-[16.5px] leading-[1.7] text-[#6B5D52] md:text-[17.5px]" data-id="element-10004" data-mp="2r9hm5qpw7947">
              {item.summary}
            </p>

            {/* The same two actions, in the same order and treatment, as every
             other surface on the site. */}
            <div className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" data-id="element-10005" data-mp="2mawl5p2tglep">
              <WaitlistCTA analyticsLabel={`feature-${item.slug}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:bg-[#C2410C]" data-id="element-10006" data-mp="28b4zr39z02qo">
                Get started free <ArrowRight size={16} data-id="element-10007" data-mp="1tqssq8j3ribl" />
              </WaitlistCTA>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full border border-[#E7E0D4] bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-colors duration-200 ease-out hover:border-[#D9CFBE] hover:bg-[#FFF8F3]" data-id="element-10008" data-mp="1orfvctulusur">
                Talk to sales
              </a>
            </div>
            <p className="mt-6 text-[13.5px] text-[#A2917F]" data-id="element-10009" data-mp="27no5oc2h5twx">
              $100 in credits free · No card required
            </p>
          </SectionIntro>
        </div>
      </section>

      {item.slug === 'sharing' && <section className="border-t border-[#EDE6DA] bg-white py-20 md:py-28" data-id="element-10010" data-mp="2hfttraplvr6u">
          <div className="mx-auto max-w-5xl px-6 md:px-10" data-id="element-10011" data-mp="2vdidgf7xssab">
            <SectionIntro align="left" className="mb-10 max-w-2xl" data-id="element-10012" data-mp="2qf8kmb9oq3rv">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#EA580C]" data-id="element-10013" data-mp="2qh1sftw1au1g">
                One sharing model
              </p>
              <h2 className="mt-3 font-title text-[32px] font-bold leading-[1.1] tracking-[-0.025em] text-[#2B211A] md:text-[44px]" data-id="element-10014" data-mp="5nlr19m23pm9">
                Connections, files, and Skills.
                <br data-id="element-10015" data-mp="fm1f13pseirj" />
                Shared where they belong.
              </h2>
            </SectionIntro>
            <TeamSharingVisual data-id="element-10016" data-mp="3e3yn4yikfde6" />
          </div>
        </section>}

      {/* ── The thesis ────────────────────────────────────────────────────
       The first section is the argument the feature makes. On the old page it
       sat in a card the same weight as everything below it; here it gets
       display type and the full width, so the page has something that wins. */}
      {lead && <section className="border-t border-[#EDE6DA] bg-white py-16 md:py-24" data-id="element-10017" data-mp="3suecmpi0l7i5">
          <div className="mx-auto max-w-6xl px-6 md:px-10" data-id="element-10018" data-mp="2vcxh5qj12hl4">
            <SectionIntro align="left" className="grid gap-8 md:grid-cols-12 md:gap-14" data-id="element-10019" data-mp="30b8nvse10bz4">
              <h2 className="font-title text-[28px] font-bold leading-[1.12] tracking-[-0.025em] text-[#2B211A] md:col-span-6 md:text-[40px]" data-id="element-10020" data-mp="2st12133q2s8o">
                {/* A short rule rather than a check icon. The old template put
                 the same orange tick beside every heading, which claimed each
                 one was a benefit being ticked off; most of them are simply
                 how the thing works. */}
                <span aria-hidden="true" className="mb-6 block h-[3px] w-10 rounded-full bg-[#EA580C]" data-id="element-10021" data-mp="29uorbcx5xaaz" />
                {lead.heading}
              </h2>
              <p className="text-[17px] leading-[1.75] text-[#6B5D52] md:col-span-6 md:text-[18.5px]" data-id="element-10022" data-mp="203u5g9ntwcq2">
                {lead.body}
              </p>
            </SectionIntro>
          </div>
        </section>}

      {/* ── The detail ────────────────────────────────────────────────────
       Hairline-ruled rows, not a wall of bordered cards. Same information,
       roughly half the ink, and the eye can run down a single left edge
       instead of re-entering a new box for every point. */}
      {rest.length > 0 && <section className="border-t border-[#EDE6DA] bg-[#FAF8F4] py-16 md:py-24" data-id="element-10023" data-mp="hn0n3jtbodj1">
          <div className="mx-auto max-w-6xl px-6 md:px-10" data-id="element-10024" data-mp="wb56kq078t2v">
            <div className="border-t border-[#E4DACB]" data-id="element-10025" data-mp="walmp3qznum7">
              {rest.map((section, index) => <SectionIntro key={section.heading} align="left" data-id="element-10026" data-mp="rctwqf9wmikn" data-mpi={index}>
                  <article className="grid gap-3 border-b border-[#E4DACB] py-8 md:grid-cols-12 md:gap-14 md:py-10" data-id="element-10027" data-mp="3m7qmtjgn8da">
                    <div className="md:col-span-5" data-id="element-10028" data-mp="3uu28qihyao7g">
                      <div className="flex items-baseline gap-4" data-id="element-10029" data-mp="cnfl10iuvp23">
                        {/* Position, not rank. The number is what lets a reader
                         say "the third one" out loud; it is set small and warm
                         grey so it never competes with the heading. */}
                        <span aria-hidden="true" className="w-6 shrink-0 font-title text-[13px] font-bold tabular-nums text-[#C4B4A2]" data-id="element-10030" data-mp="1k0zgayp821iw">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <h3 className="font-title text-[21px] font-bold leading-[1.2] tracking-[-0.015em] text-[#2B211A] md:text-[24px]" data-id="element-10031" data-mp="1wjv3p3ldrkht">
                          {section.heading}
                        </h3>
                      </div>
                    </div>
                    <p className="pl-10 text-[16px] leading-[1.7] text-[#6B5D52] md:col-span-7 md:pl-0 md:text-[17px]" data-id="element-10032" data-mp="1mn8igdp56rf9">
                      {section.body}
                    </p>
                  </article>
                </SectionIntro>)}
            </div>
          </div>
        </section>}

      {/* ── Siblings ──────────────────────────────────────────────────────
       The rest of the group, rather than one arbitrary "next feature". These
       pages are read as a set — someone who has just understood Tracks is
       deciding whether they also need Groups — so the foot of the page should
       be the set, not a single link into the middle of it. */}
      {siblings.length > 0 && <section className="border-t border-[#EDE6DA] bg-white py-16 md:py-20" data-id="element-10033" data-mp="37ugkkciw8x0h">
          <div className="mx-auto max-w-6xl px-6 md:px-10" data-id="element-10034" data-mp="32xzlina5oy8m">
            <SectionIntro align="left" data-id="element-10035" data-mp="2yvcz51w985it">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#A2917F]" data-id="element-10036" data-mp="2yu1mecktznfk">
                More in {group?.label}
              </p>
              <ul className="mt-6 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10037" data-mp="2oy8dr4a8ge32">
                {siblings.map(sib => <li key={sib.slug} data-id="element-10038" data-mp="2k6gjojrvbq86">
                    <Link to={`/features/${sib.slug}`} className="group flex items-start gap-4 border-b border-[#EDE6DA] py-5 transition-colors duration-200 ease-out" data-id="element-10039" data-mp="2k5faylybrftm">
                      <span aria-hidden="true" className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl border border-[#EAE0D2] bg-[#FAF8F4] text-[#A2917F] transition-colors duration-200 ease-out group-hover:border-[#F2C6A6] group-hover:text-[#EA580C]" data-id="element-10040" data-mp="2b73bdufhd7p1">
                        <FeatureIcon name={sib.icon} size={17} data-id="element-10041" data-mp="gqbvspca2atw" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-10042" data-mp="lp6c9kmrvnau">
                        <span className="block font-title text-[17px] font-bold tracking-[-0.01em] text-[#2B211A] transition-colors duration-200 ease-out group-hover:text-[#EA580C]" data-id="element-10043" data-mp="lnljo0k0qefm">
                          {sib.name}
                        </span>
                        <span className="mt-1 block text-[14px] leading-snug text-[#8A7B6D]" data-id="element-10044" data-mp="3u588dz4m2i6s">
                          {sib.eyebrow}
                        </span>
                      </span>
                      <ArrowRight size={16} className="mt-1 shrink-0 text-[#C4B4A2] transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:text-[#EA580C]" data-id="element-10045" data-mp="70ildrnvg1r8" />
                    </Link>
                  </li>)}
              </ul>
            </SectionIntro>
          </div>
        </section>}

      {/* ── Close ─────────────────────────────────────────────────────────
       The same ink panel, actions and offer the home page ends on, so the
       bottom of a feature page is the bottom of the site rather than a
       secondary "next feature" strip that trailed off. */}
      <section className="relative overflow-hidden bg-[#1A120C] py-20 text-white md:py-28" data-id="element-10046" data-mp="361a0mhijh7pe">
        <ScrubVideoPlate src={CLOSING_VIDEO} poster={CLOSING_POSTER} data-id="element-10047" data-mp="36335br9ldhur" />
        <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-10048" data-mp="3198r33cr9v32" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-10049" data-mp="3f41f6q6pojl9">
          <SectionIntro align="left" className="max-w-2xl" data-id="element-10050" data-mp="3a22938jl78km">
            <h2 className="font-title text-[32px] font-bold leading-[1.06] tracking-[-0.03em] text-[#FAF8F4] md:text-[48px]" data-id="element-10051" data-mp="3ozuom0p4vwyt">
              Put {item.name} to work this week.
            </h2>
            <p className="mt-5 text-[16.5px] leading-[1.7] text-white/60" data-id="element-10052" data-mp="1um3jnehr219p">
              Connect your tools once and let Giga run alongside the systems your
              teams use every day.
            </p>
            <div className="mt-9 flex flex-col items-stretch gap-x-8 gap-y-4 sm:flex-row sm:items-center" data-id="element-10053" data-mp="557ypi1ctjhg">
              <WaitlistCTA analyticsLabel={`feature-${item.slug}-close`} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-10054" data-mp="a4khnrp3u6er">
                Get started free <ArrowRight size={16} data-id="element-10055" data-mp="3iit8ovbzap4t" />
              </WaitlistCTA>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:text-white/70" data-id="element-10056" data-mp="3ml7qrd7tpkv1">
                Talk to sales
              </a>
            </div>
            <p className="mt-7 text-[13px] text-white/45" data-id="element-10057" data-mp="16ul4vm9tsety">
              $100 in credits included · no card needed
            </p>
          </SectionIntro>

          {/* One quiet link on to the next page, kept where a link belongs —
           after the offer, not competing with it. */}
          <div className="relative mt-14 border-t border-white/10 pt-6" data-id="element-10058" data-mp="15d5z2hh7c74j">
            <Link to={`/features/${next.slug}`} className="group inline-flex items-center gap-2 text-[14px] font-bold text-white/55 transition-colors duration-200 ease-out hover:text-white" data-id="element-10059" data-mp="15buz6sqvnjip">
              Next: {next.name}
              <ArrowRight size={15} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-10060" data-mp="1t2quw0ygeyv8" />
            </Link>
          </div>
        </div>
      </section>
    </>;
}